import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ParallaxComponent from './ParallaxComponent';
import MaterialIcon from './MaterialIcon';
import parallaxService from '../services/parallaxService';
import './ParallaxTest.css';

const ParallaxTest = () => {
  const [showAdmin, setShowAdmin] = useState(true);
  const [clickedData, setClickedData] = useState(null);
  const [savedScenes, setSavedScenes] = useState([]);
  const [isLoadingScenes, setIsLoadingScenes] = useState(false);
  
  // Scene stickers - will be populated by loaded scenes
  const [sceneStickers1, setSceneStickers1] = useState([]);
  const [sceneStickers2, setSceneStickers2] = useState([]);
  
  // Track current scene names for each instance
  const [currentSceneName1, setCurrentSceneName1] = useState('clean-test-1');
  const [currentSceneName2, setCurrentSceneName2] = useState('clean-test-2');

  const handleStickerClick = (data) => {
    setClickedData(data);
  };

  const handleAdminToggle = (isOpen) => {
    // Admin panel toggled
  };

  const handleSceneStickersUpdate1 = (newStickers) => {
    setSceneStickers1(newStickers);
  };

  const handleSceneStickersUpdate2 = (newStickers) => {
    setSceneStickers2(newStickers);
  };

  // Callbacks to update scene names when scenes are loaded
  const handleSceneNameUpdate1 = (sceneName) => {
    setCurrentSceneName1(sceneName || 'clean-test-1');
  };

  const handleSceneNameUpdate2 = (sceneName) => {
    setCurrentSceneName2(sceneName || 'clean-test-2');
  };

  return (
    <div className="parallax-test">
      {/* Header */}
      <div className="test-header">
        <h1>🧹 Clean Parallax Test - Multi-Instance</h1>
        <p>Testing instance-specific scene assignments with database storage</p>
      </div>

      {/* Test Controls */}
      <div className="test-controls">
        <div className="control-group">
          <label>
            <input
              type="checkbox"
              checked={showAdmin}
              onChange={(e) => setShowAdmin(e.target.checked)}
            />
            Show Admin Panel
          </label>
        </div>
        
        <div className="control-group">
          <button onClick={() => window.location.reload()}>
            🔄 Reset Page
          </button>
        </div>
      </div>

      {/* Test Instructions */}
      <div className="test-instructions">
        <h3>📋 Test Instructions</h3>
        <ol>
          <li>Each parallax instance maintains its own scene assignment in the database</li>
          <li>Create different scenes in each instance and save them</li>
          <li>Refresh the page - each instance should load its assigned scene</li>
          <li>Use the admin panel to see which scene each instance is currently showing</li>
          <li>Try loading different scenes in each instance - they should remain independent</li>
        </ol>
      </div>

      {/* Spacer for scroll testing */}
      <div style={{ height: '100vh', background: 'linear-gradient(45deg, #333, #666)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Scroll Down to Test Multi-Instance Parallax</h2>
          <p>Each instance below will maintain its own scene assignment</p>
        </div>
      </div>

      {/* First Parallax Component */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ textAlign: 'center', color: 'white', marginBottom: '10px' }}>Instance 1: {currentSceneName1}</h3>
        <ParallaxComponent
          category="dinosaurs"
          availableSizes={['ATOM', 'PIXEL', 'NANO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE']}
          height="60vh"
          instanceId="clean-test-1"
          sceneStickers={sceneStickers1}
          onStickerClick={handleStickerClick}
          onSceneStickersUpdate={handleSceneStickersUpdate1}
          onSceneNameUpdate={handleSceneNameUpdate1}
          showAdmin={showAdmin}
          onAdminToggle={handleAdminToggle}
        />
      </div>

      {/* Second Parallax Component */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ textAlign: 'center', color: 'white', marginBottom: '10px' }}>Instance 2: {currentSceneName2}</h3>
        <ParallaxComponent
          category="dinosaurs"
          availableSizes={['ATOM', 'PIXEL', 'NANO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE']}
          height="60vh"
          instanceId="clean-test-2"
          sceneStickers={sceneStickers2}
          onStickerClick={handleStickerClick}
          onSceneStickersUpdate={handleSceneStickersUpdate2}
          onSceneNameUpdate={handleSceneNameUpdate2}
          showAdmin={showAdmin}
          onAdminToggle={handleAdminToggle}
        />
      </div>

      {/* Spacer for more scroll testing */}
      <div style={{ height: '100vh', background: 'linear-gradient(45deg, #666, #999)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Scroll Up to Test Parallax</h2>
          <p>Each instance should maintain its own scene independently</p>
        </div>
      </div>

      {/* Click Data Display */}
      {clickedData && (
        <div className="click-data">
          <h3>🎯 Last Clicked Sticker</h3>
          <pre>{JSON.stringify(clickedData, null, 2)}</pre>
          <button onClick={() => setClickedData(null)}>Clear</button>
        </div>
      )}

      {/* Test Results */}
      <div className="test-results">
        <h3>✅ Database-Based Instance Management</h3>
        <ul>
          <li>✅ Instance-scene assignments stored in database (not localStorage)</li>
          <li>✅ Each instance maintains independent scene assignments</li>
          <li>✅ Scene assignments persist across page reloads</li>
          <li>✅ Admin panel shows current scene for each instance</li>
          <li>✅ Clear scene assignment functionality</li>
          <li>✅ Foreign key constraints ensure data integrity</li>
        </ul>
      </div>
    </div>
  );
};

// ParallaxTest has no props - it's a standalone page component
ParallaxTest.propTypes = {};

export default ParallaxTest; 